import { Component, computed, inject, input } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ValidationPhaseError, ValidationPhaseResponse } from '../../../api/app-types/validation.app';
import { mapAssignmentErrorPropertyToRouteSegments } from '../../../shared/helpers/routing.func';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createInfoNotification } from '../../../shared/notification/notification.factory';

@Component({
	selector: 'lvr-assignment-checks',
	templateUrl: './assignment-checks.html',
	styleUrl: './assignment-checks.scss',
	imports: [MatIcon, TranslocoPipe, RouterLink],
})
export class AssignmentChecks extends RouteInjection {
	private readonly _routeData = undefined as unknown as {
		route: (string | number)[];
		toastMessageKey?: string;
	};
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	public readonly errors = input.required<ValidationPhaseResponse>();
	public readonly errorAreas = computed(() => Object.keys(this.errors()));
	public readonly assignmentId = input.required<number | string>();
	public readonly clientId = input.required<number | string>();
	public readonly institutionId = input<number | string>();
	public readonly mainInstitutionId = input<number | string>();
	public readonly isMainInstitution = input<boolean>();
	public readonly reportId = input<number | string>();
	public readonly careerId = input<number | string>();
	public readonly isServiceOfficeStatement = input<boolean>(false);
	public readonly isAdditionalAssignment = input<boolean>(false);
	public readonly disabilityReportId = input<number>();
	public readonly targetPhase = input<number | string>();

	public readonly queryParams = {
		redirectUrl: this.router.url,
	};

	public getRoute(propertyName: string, subPropertyNames?: string[], message?: string) {
		return mapAssignmentErrorPropertyToRouteSegments(
			propertyName,
			this.assignmentId(),
			this.clientId(),
			this.institutionId(),
			this.reportId(),
			this.isServiceOfficeStatement(),
			this.isAdditionalAssignment(),
			subPropertyNames,
			message,
			this.disabilityReportId(),
			this.careerId(),
			this.isMainInstitution(),
			this.mainInstitutionId(),
			this.targetPhase(),
		) as typeof this._routeData;
	}

	public getErrorsOfArea(area: string) {
		const errors: ValidationPhaseError[] = (this.errors()[area] || []).filter(
			(e) => e.severity.toLowerCase() === 'error',
		);

		const errorsWithNav = errors.map((e) => {
			const routeInfo = this.getRoute(area, [e.property], e.message);

			return {
				...e,
				route: routeInfo.route,
				toastMessageKey: routeInfo.toastMessageKey,
				validLink: routeInfo.route?.[0] !== '/unknown-error',
			};
		});

		return { errors: errorsWithNav };
	}

	public showToastMessage(toastMessageKey?: string) {
		if (!toastMessageKey) return;
		this.ntfy.addNotification(createInfoNotification(this.transloco.translate(toastMessageKey)));
	}
}
